<template>
  <v-card class="ma-2" outlined>
    <v-card-title>パーソナルシート（学習用メモ）</v-card-title>
    <v-card-text>
      <p>HOMEで入力した学習用メモです。上のグラフ・計画合計金額は商品選択の結果であり、このシートとは別に集計しています。</p>
      <dl class="sheet-details">
        <div v-for="field in fields" :key="field.key">
          <dt>{{ field.label }}</dt>
          <dd>{{ fieldValue(field) }}</dd>
        </div>
      </dl>
      <p class="mt-4">上下限は自動計算したNMIを基準に計算しています。ラッキーカードは含めません。</p>
      <v-simple-table dense class="sheet-summary-table">
        <template v-slot:default>
          <caption class="text-left pb-2">各費目の予算と決定額（円）</caption>
          <thead><tr><th scope="col">費目</th><th scope="col">下限</th><th scope="col">自分で決めた金額</th><th scope="col">上限</th></tr></thead>
          <tbody>
            <tr v-for="row in rows" :key="row.key">
              <th scope="row">{{ row.label }}</th>
              <td>{{ yen(row.lowerAmount) }}<small>{{ row.lowerRate }}％</small></td>
              <td class="sheet-decision">{{ amountValue(sheet.amounts[row.key]) }}</td>
              <td v-if="row.upperRate === null">上限なし</td>
              <td v-else>{{ yen(row.upperAmount) }}<small>{{ row.upperRate }}％</small></td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <div v-if="showTotals" class="sheet-summary-totals mt-4 pa-4">
        <div><span>支出合計（入力済み {{ totals.entered }} / {{ rows.length }}）</span><strong>{{ yen(totals.total) }}</strong></div>
        <div><span>残金</span><strong :class="{ 'error--text': totals.balance !== null && totals.balance < 0 }">{{ yen(totals.balance) }}</strong></div>
        <p class="text-caption mb-0">残金 ＝ NMI ＋ ラッキーカード − 支出合計（ラッキーカードの空欄は0円として計算）</p>
        <p v-if="totals.balance === null" class="text-caption mb-0">NMIの計算に必要な入力が不足しているか、入力エラーがあるため、残金は計算していません。</p>
        <p v-if="totals.entered < rows.length" class="text-caption mb-0">未入力・入力エラーの費目は合計に含めていません。</p>
      </div>
      <p v-else class="mt-4">パーソナルシートの合計・残金は未確認、または計算に必要な入力が不足しています。</p>
    </v-card-text>
  </v-card>
</template>

<script>
import { PERSONAL_FIELDS, resolvedPersonalSheet, parseSheetAmount, personalSheetRows, personalSheetTotals, canDisplayPersonalSheetTotals } from '../utils/personal-sheet'

export default {
  data: () => ({ fields: PERSONAL_FIELDS.filter(field => !field.hidden) }),
  computed: {
    showTotals() { return canDisplayPersonalSheetTotals(this.sheet) },
    sheet() { return resolvedPersonalSheet(this.$store.state) },
    rows() { return personalSheetRows(this.sheet, this.$store.state.login_response, this.$store.state.target) },
    totals() { return personalSheetTotals(this.sheet) }
  },
  methods: {
    yen(value) { return value === null ? '—' : value.toLocaleString('ja-JP') + '円' },
    amountValue(value, signed = false, yen = true) {
      if (value === '' || value === null || value === undefined) return '未入力'
      const amount = parseSheetAmount(value, signed)
      if (amount === null) return `${value}（入力エラー）`
      return yen ? this.yen(amount) : String(amount)
    },
    fieldValue(field) {
      const value = this.sheet.fields[field.key]
      if (field.key === 'spouse_age') {
        if (this.sheet.fields.is_married === 'いない') return 'いない'
        if (value) return `${value}歳`
        return this.sheet.fields.is_married === 'いる' ? 'いる（年齢未入力）' : '未入力'
      }
      if (field.key.startsWith('child') && String(value).trim() === 'いない') return 'いない'
      if (field.numeric) {
        const display = this.amountValue(value, field.signed, !!field.yen)
        return field.unit && parseSheetAmount(value, field.signed) !== null ? display + field.unit : display
      }
      return value === '' || value === null || value === undefined ? '未入力' : value
    }
  }
}
</script>

<style scoped>
.sheet-details { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 24px; }
.sheet-details dt { font-size: 12px; color: #546e7a; }
.sheet-details dd { margin: 0; color: #263238; overflow-wrap: anywhere; }
.sheet-summary-table ::v-deep table { border-collapse: collapse; }
.sheet-summary-table th, .sheet-summary-table td { padding: 8px !important; border: 1px solid #b0bec5 !important; }
.sheet-summary-table thead th { text-align: center !important; background: #eceff1; color: #263238 !important; font-weight: 700; }
.sheet-summary-table tbody th { text-align: left; font-weight: 500; }
.sheet-summary-table td { text-align: right; overflow-wrap: anywhere; }
.sheet-summary-table small { display: block; color: #546e7a; }
.sheet-decision { background: #fffde7; }
.sheet-summary-totals { background: #f5f7f8; color: #263238; }
.sheet-summary-totals > div { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 8px; flex-wrap: wrap; }
@media (max-width: 599px) {
  .sheet-summary-table th, .sheet-summary-table td { font-size: 12px !important; padding: 6px 4px !important; }
}
</style>
